import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Globe2, 
  Palette, 
  Building2, 
  ShoppingCart, 
  Settings,
  Phone,
  Mail
} from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service data
const servicesData = {
  "showcase-site": {
    icon: <Globe2 className="w-8 h-8" />,
    title: "Showcase Site",
    price: "499",
    pages: "Up to 5 pages",
    description: "At Webcraft, each website is custom-designed to accurately reflect your company's identity. We prioritize modern design, intuitive navigation, and an efficient structure to provide a smooth and professional user experience.",
    features: [
      "A personalized and professional design",
      "A clear presentation of your company and services",
      "Simple and intuitive navigation",
      "Full compatibility on mobile, tablet, and computer",
      "Basic search engine optimization (SEO)",
      "A functional contact page to facilitate communication"
    ],
    bottomText: "Creating a website with Webcraft provides you with a reliable and elegant presentation tool, designed to enhance your credibility and give your business a professional online presence.",
    idealFor: ["Small businesses", "Freelancers", "Local services", "Personal brands"]
  },
  "website-redesign": {
    icon: <Palette className="w-8 h-8" />,
    title: "Website Redesign",
    price: "399",
    description: "At Webcraft, every redesign is custom-designed. We thoroughly analyze your brand image, business objectives, and user behavior to create a website with refined aesthetics, optimized for speed, security, and SEO. Every detail is carefully considered to strengthen your credibility and maximize the impact of your online presence.",
    features: [
      "A modern, high-end design consistent with your brand identity",
      "An intuitive and engaging user experience",
      "Optimal performance on all devices",
      "An advanced SEO structure for increased visibility",
      "A website designed to convert and inspire trust"
    ],
    bottomText: "A website redesign is a strategic investment that transforms your site into a true growth driver, reflecting the excellence of your business.",
    idealFor: ["Outdated websites", "Rebranding projects", "Performance improvements", "Conversion optimization"]
  },
  "website-business": {
    icon: <Building2 className="w-8 h-8" />,
    title: "Website Business",
    price: "899",
    pages: "Up to 10 pages",
    featured: true,
    description: "At Webcraft, every business website is custom-designed to reflect your company's identity and meet your business objectives. We create structured, elegant, and high-performing websites designed to inspire trust and facilitate contact with your clients.",
    features: [
      "A professional and personalized design that reflects your brand",
      "A clear structure that showcases your services and expertise",
      "Smooth navigation and an optimized user experience",
      "Full compatibility with mobile, tablet, and desktop devices",
      "Basic search engine optimization (SEO)",
      "An effective contact form for lead generation"
    ],
    bottomText: "Creating a business website with Webcraft provides you with a powerful, reliable, and scalable communication tool designed to support your company's growth and credibility.",
    idealFor: ["Growing businesses", "Professional services", "B2B companies", "Established brands"]
  },
  "website-ecommerce": {
    icon: <ShoppingCart className="w-8 h-8" />,
    title: "Website E-commerce",
    price: "1,499",
    pages: "Unlimited pages",
    description: "At Webcraft, we design custom online stores, combining premium design, intuitive navigation, and reliable technologies. Each project is designed to showcase your products, simplify the customer journey, and ensure efficient management of sales, payments, and orders.",
    features: [
      "A modern, elegant online store tailored to your brand",
      "A seamless shopping experience on desktop, tablet, and mobile",
      "Secure payments and comprehensive product management",
      "A structure optimized for performance and SEO",
      "A website designed to convert and scale with your business",
      "Unlimited pages"
    ],
    bottomText: "Creating an e-commerce website with Webcraft transforms your online presence into a powerful, reliable sales channel ready to support your business growth.",
    idealFor: ["Online retailers", "Product-based businesses", "Dropshipping", "Multi-product stores"]
  },
  "monthly-maintenance": {
    icon: <Settings className="w-8 h-8" />,
    title: "Monthly Maintenance",
    price: "49",
    period: "/month",
    description: "At Webcraft, monthly maintenance ensures your website remains consistently up-to-date, functional, and optimized. We handle technical updates, security monitoring, backups, and any necessary adjustments to maintain peak performance.",
    features: [
      "Regular website and feature updates",
      "Security vulnerability monitoring and protection",
      "Automatic backups and rapid restoration in case of issues",
      "Continuous performance and speed optimization",
      "Technical support and peace of mind"
    ],
    bottomText: "Monthly maintenance is a proactive solution that allows you to focus on your business, knowing your website is in good hands, secure, and always performing at its best.",
    idealFor: ["All website owners", "Busy entrepreneurs", "Peace of mind seekers", "Security-conscious businesses"]
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  }
};

const ServicePage = () => {
  const { serviceId } = useParams();
  const service = servicesData[serviceId];
  
  const [formData, setFormData] = useState({
    name: "",
    business_name: "",
    email: "",
    message: `I'm interested in the ${service?.title || ''} package.`
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!service) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Service Not Found</h1>
          <Link to="/" className="text-white/60 hover:text-white transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        message: `[${service.title} Inquiry]\n\n${formData.message}`
      });
      toast.success(response.data.message);
      setFormData({ 
        name: "", 
        business_name: "", 
        email: "", 
        message: `I'm interested in the ${service.title} package.` 
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="service-page">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#FFFFFF',
          }
        }}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center" data-testid="service-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_prestige-digital-1/artifacts/l5w9fjoc_WhatsApp%20Image%202026-03-08%20at%2023.43.08.jpeg" 
                alt="Webcraft" 
                className="h-8 md:h-10 w-auto rounded"
              />
            </Link>
            <Link 
              to="/#services" 
              className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
              data-testid="back-to-services"
            >
              <ArrowLeft size={18} />
              All Services
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-50" />
        <div className="container mx-auto px-6 md:px-12 relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-4xl"
          >
            {service.featured && (
              <span className="inline-block bg-white text-black text-xs font-bold px-4 py-1 rounded-full mb-6">
                MOST POPULAR
              </span>
            )}
            
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-white/5 rounded-2xl text-white/80">
                {service.icon}
              </div>
              <div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-white" data-testid="service-title">
                  {service.title}
                </h1>
                {service.pages && (
                  <p className="text-white/40 mt-2">{service.pages}</p>
                )}
              </div>
            </div>

            <div className="flex items-baseline gap-2 mb-8">
              <span className="text-5xl md:text-6xl font-extrabold text-white">${service.price}</span>
              {service.period && (
                <span className="text-xl text-white/40">{service.period}</span>
              )}
            </div>

            <p className="text-lg md:text-xl text-white/60 leading-relaxed max-w-3xl" data-testid="service-description">
              {service.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Features */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">What's Included</h2>
              
              <div className="space-y-4" data-testid="service-features">
                {service.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/5"
                  >
                    <div className="w-6 h-6 flex items-center justify-center bg-green-500/20 rounded-full flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-green-400" />
                    </div>
                    <p className="text-white/80">{feature}</p>
                  </motion.div>
                ))}
              </div>

              {/* Ideal For */}
              <div className="mt-12">
                <h3 className="text-xl font-bold text-white mb-4">Ideal For</h3>
                <div className="flex flex-wrap gap-2">
                  {service.idealFor.map((item, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white/70 text-sm"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* Bottom Text */}
              <div className="mt-12 p-6 bg-white/5 rounded-2xl border border-white/10">
                <p className="text-white/60 leading-relaxed italic">
                  "{service.bottomText}"
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="lg:sticky lg:top-32 h-fit"
            >
              <div className="glass-card p-8 md:p-10">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Get Started Today
                </h2>
                <p className="text-white/50 mb-8">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="service-contact-form">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your Name"
                      required
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 focus:outline-none transition-colors"
                      data-testid="service-name-input"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      placeholder="Business Name"
                      required
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 focus:outline-none transition-colors"
                      data-testid="service-business-input"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      required
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 focus:outline-none transition-colors"
                      data-testid="service-email-input"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project..."
                      required
                      rows={4}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 focus:outline-none transition-colors resize-none"
                      data-testid="service-message-input"
                    />
                  </div>
                  <motion.button
                    type="submit"
                    className="w-full bg-white text-black px-8 py-4 rounded-full font-bold flex items-center justify-center gap-2 hover:bg-white/90 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isSubmitting}
                    data-testid="service-submit-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <>
                        Request Quote
                        <ArrowRight size={18} />
                      </>
                    )}
                  </motion.button>
                </form>

                {/* Contact Info */}
                <div className="mt-8 pt-8 border-t border-white/10">
                  <p className="text-white/40 text-sm mb-4">Or contact us directly:</p>
                  <div className="space-y-3">
                    <a 
                      href="tel:+15819848633" 
                      className="flex items-center gap-3 text-white/60 hover:text-white transition-colors"
                    >
                      <Phone size={16} />
                      (581) 984-8633
                    </a>
                    <a 
                      href="mailto:webcraftbusinesses@gmail.com" 
                      className="flex items-center gap-3 text-white/60 hover:text-white transition-colors"
                    >
                      <Mail size={16} />
                      webcraftbusinesses@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">Other Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(servicesData)
              .filter(([key]) => key !== serviceId)
              .map(([key, svc]) => (
                <Link
                  key={key}
                  to={`/services/${key}`}
                  className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-all group"
                  data-testid={`other-service-${key}`}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg mb-4 text-white/60 group-hover:text-white transition-colors">
                    {svc.icon}
                  </div>
                  <h3 className="text-white font-semibold mb-1">{svc.title}</h3>
                  <p className="text-white/40 text-sm">
                    ${svc.price}{svc.period || ''}
                  </p>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#050505] border-t border-white/5">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/30 text-sm">
              © {new Date().getFullYear()} Webcraft. All rights reserved.
            </p>
            <Link to="/" className="text-white/40 hover:text-white/60 transition-colors text-sm">
              Back to Home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicePage;
